import React, { createContext, ReactNode } from 'react';
import { useFetchMessages } from '@src/components/node-info/hooks/useFetchMessages.hook';
import { useFetchTaskManagements } from '@src/components/node-info/hooks/useFetchTaskManagements.hook';
import { useFetchProfileFields, type ProfileField } from '@src/components/node-info/hooks/useFetchProfileFields.hook';
import { useFetchOnboardingQuestions, type OnboardingQuestion } from '@src/components/node-info/hooks/useFetchOnboardingQuestions.hook';

// Define the shape of our context data
export interface BehavioralRulesData {
  allMessages: { message: string; scenarioId: string }[];
  taskManagements: { id: string; title: string; type: string; description: string }[];
  profileFields: ProfileField[];
  onboardingQuestions: OnboardingQuestion[];
  isLoadingMessages: boolean | null;
  isLoadingTaskManagements: boolean | null;
  isLoadingProfileFields: boolean | null;
  isLoadingOnboardingQuestions: boolean | null;
}

// Define the return types of our hooks more precisely
type MessagesHookReturn = ReturnType<typeof useFetchMessages>;
type TaskManagementsHookReturn = ReturnType<typeof useFetchTaskManagements>;
type ProfileFieldsHookReturn = ReturnType<typeof useFetchProfileFields>;
type OnboardingQuestionsHookReturn = ReturnType<typeof useFetchOnboardingQuestions>;

// Create the context with a default undefined value
export const BehavioralRulesDataContext = createContext<BehavioralRulesData | undefined>(undefined);

interface BehavioralRulesDataProviderProps {
  children: ReactNode;
  // Optional callbacks for testing or custom implementations
  messageDataProvider?: () => MessagesHookReturn;
  taskManagementDataProvider?: () => TaskManagementsHookReturn;
  // LF360-1828: Participant Profile combobox source
  profileFieldDataProvider?: () => ProfileFieldsHookReturn;
  // LF360-1828: Onboarding Responses combobox source
  onboardingQuestionDataProvider?: () => OnboardingQuestionsHookReturn;
}

export const BehavioralRulesDataProvider: React.FC<BehavioralRulesDataProviderProps> = ({
  children,
  messageDataProvider = useFetchMessages,
  taskManagementDataProvider = useFetchTaskManagements,
  profileFieldDataProvider = useFetchProfileFields,
  onboardingQuestionDataProvider = useFetchOnboardingQuestions,
}) => {
  const [allMessages, isLoadingMessages] = messageDataProvider();
  const [taskManagements, isLoadingTaskManagements] = taskManagementDataProvider();
  const [profileFields, isLoadingProfileFields] = profileFieldDataProvider();
  const [onboardingQuestions, isLoadingOnboardingQuestions] = onboardingQuestionDataProvider();

  // Provide the consolidated data to consumers
  const contextValue: BehavioralRulesData = {
    allMessages: allMessages as { message: string; scenarioId: string }[],
    taskManagements: taskManagements as { id: string; title: string; type: string; description: string }[],
    profileFields: profileFields as ProfileField[],
    onboardingQuestions: onboardingQuestions as OnboardingQuestion[],
    isLoadingMessages: isLoadingMessages as boolean | null,
    isLoadingTaskManagements: isLoadingTaskManagements as boolean | null,
    isLoadingProfileFields: isLoadingProfileFields as boolean | null,
    isLoadingOnboardingQuestions: isLoadingOnboardingQuestions as boolean | null,
  };

  return (
    <BehavioralRulesDataContext.Provider value={contextValue}>
      {children}
    </BehavioralRulesDataContext.Provider>
  );
};

// Helper hook for consuming the context
export const useBehavioralRulesData = (): BehavioralRulesData => {
  const context = React.useContext(BehavioralRulesDataContext);
  if (!context) {
    throw new Error('useBehavioralRulesData must be used within a BehavioralRulesDataProvider');
  }
  return context;
};
